import { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Car,
  UtensilsCrossed,
  Loader2,
  Check,
  Leaf,
  Camera,
  Share2,
  MapPin,
  Lock,
  Heart,
  Star
} from '@/ui/icons'
import { cn } from '@/shared/utils';
import { MEDIA_CONFIG } from '@/shared/utils/media';

export function RoutePlanningAnimation(): JSX.Element {
  const waypointConfigs = useMemo(
    () => [
      {
        delay: 0.2,
        position: 'left-8 top-44',
        label: 'Meet Up Location'
      },
      {
        delay: 0.6,
        position: 'left-24 top-16',
        label: 'Viewpoint'
      },
      {
        delay: 1.0,
        position: 'left-40 top-52',
        label: 'Lunch Spot'
      },
      {
        delay: 1.4,
        position: 'left-64 top-20',
        label: 'Camp Spot'
      }
    ],
    []
  );

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-earth-200 bg-white p-6 text-earth-900 shadow-xl dark:border-earth-700 dark:bg-earth-900 dark:text-primary-foreground">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(92,141,197,0.14),_transparent_60%)] dark:bg-[radial-gradient(circle_at_top,_rgba(92,141,197,0.25),_transparent_60%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(92,141,197,0.08)_25%,transparent_25%,transparent_50%,rgba(92,141,197,0.08)_50%,rgba(92,141,197,0.08)_75%,transparent_75%,transparent)] bg-[length:28px_28px] dark:bg-[linear-gradient(135deg,rgba(255,255,255,0.04)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.04)_50%,rgba(255,255,255,0.04)_75%,transparent_75%,transparent)]" />

      <div className="relative h-64 w-full">
        <motion.svg
          viewBox="0 0 320 240"
          className="h-full w-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
        >
          <motion.path
            d="M40 200 C80 120 140 180 180 120 C220 60 260 80 280 40"
            fill="none"
            stroke="hsl(var(--adventure-400))"
            strokeWidth={6}
            strokeLinecap="round"
            strokeDasharray="12 16"
            animate={{ strokeDashoffset: [0, -200] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
          />
        </motion.svg>

        <motion.div
          className="absolute left-0 top-0"
          animate={{
            x: [24, 96, 144, 196, 248],
            y: [184, 132, 176, 108, 52],
            rotate: [6, -8, 10, -6, 8]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div className="relative -left-5 -top-5 flex h-10 w-10 items-center justify-center rounded-full bg-adventure-400 text-white shadow-md dark:text-earth-900">
            <Car className="h-5 w-5" />
          </div>
        </motion.div>

        {waypointConfigs.map((config, index) => (
          <motion.div
            key={config.label}
            className={cn(
              'pointer-events-none absolute flex flex-col items-center gap-2',
              config.position
            )}
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: [0.4, 1, 1], opacity: [0, 1, 1] }}
            transition={{
              duration: 0.9,
              delay: config.delay,
              repeat: Infinity,
              repeatDelay: 3
            }}
          >
            <motion.span
              className="whitespace-nowrap rounded-md bg-white/95 px-3 py-1 text-xs font-medium text-earth-700 shadow-md backdrop-blur dark:bg-earth-900/85 dark:text-earth-100"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: [0, 1, 1], y: [8, 0, 0] }}
              transition={{
                duration: 0.7,
                delay: config.delay + 0.15,
                repeat: Infinity,
                repeatDelay: 3
              }}
            >
              {config.label}
            </motion.span>
            <div className="h-2 w-2 rounded-full bg-adventure-300" />
          </motion.div>
        ))}
      </div>

      <motion.div
        className="relative mt-6 flex items-center justify-between rounded-xl bg-earth-50/90 p-4 shadow-inner backdrop-blur dark:bg-earth-800/80"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div>
          <p className="text-xs uppercase tracking-widest text-earth-500 dark:text-earth-100/70">
            Shared itinerary
          </p>
          <motion.p
            className="mt-1 text-sm font-medium text-earth-700 dark:text-earth-100"
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          >
            Waypoints and stops synced for the crew
          </motion.p>
        </div>
        <motion.div
          className="flex items-center gap-2 rounded-lg bg-adventure-500/10 px-3 py-1.5 text-adventure-600 dark:text-adventure-200"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Check className="h-4 w-4" />
          <span className="text-xs font-medium">Up to date</span>
        </motion.div>
      </motion.div>
    </div>
  );
}

export function MealPlanningAnimation(): JSX.Element {
  const days = useMemo(
    () => ['Thu', 'Fri', 'Sat', 'Sun'],
    []
  );

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-earth-200 bg-white p-6 text-earth-900 shadow-xl dark:border-earth-700 dark:bg-earth-900 dark:text-primary-foreground">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,_rgba(92,141,197,0.14),_transparent_60%)] dark:bg-[radial-gradient(circle_at_bottom,_rgba(92,141,197,0.25),_transparent_60%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(92,141,197,0.08)_1px,transparent_1px),linear-gradient(0deg,rgba(92,141,197,0.08)_1px,transparent_1px)] bg-[size:32px_32px] dark:bg-[linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(0deg,rgba(255,255,255,0.04)_1px,transparent_1px)]" />
      <div className="relative grid gap-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-earth-500 dark:text-earth-100/60">
              Crew Meal Planner
            </p>
            <h3 className="text-lg font-semibold text-earth-900 dark:text-earth-50">
              Mojave Adventure
            </h3>
          </div>
          <motion.div
            className="rounded-full bg-adventure-500/10 p-3 text-adventure-600 dark:text-adventure-200"
            animate={{ rotate: [0, 12, -12, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <UtensilsCrossed className="h-5 w-5" />
          </motion.div>
        </div>

        <div className="grid grid-cols-4 gap-3">
          {days.map((day, index) => (
            <motion.div
              key={day}
              className="flex flex-col gap-2 rounded-xl border border-earth-200 bg-earth-50 p-3 shadow-sm dark:border-earth-700/60 dark:bg-earth-800/80"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
            >
              <span className="text-xs font-semibold uppercase text-earth-500 dark:text-earth-200">
                {day}
              </span>
              <motion.div
                className="rounded-lg bg-white px-2 py-1 text-xs text-earth-700 dark:bg-earth-900/70 dark:text-earth-100"
                animate={{ opacity: [0.6, 1, 0.6] }}
                transition={{ duration: 3, repeat: Infinity, delay: index * 0.6 }}
              >
                {index === 0 && 'Campfire Chili'}
                {index === 1 && 'Trailhead Tacos'}
                {index === 2 && 'Dutch Oven Cobbler'}
                {index === 3 && 'Cast Iron Breakfast'}
              </motion.div>
              <motion.span
                className="text-xs text-earth-500 dark:text-earth-400"
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
              >
                {index === 0 && 'Cook: Alex'}
                {index === 1 && 'Cook: Priya'}
                {index === 2 && 'Cook: Sam'}
                {index === 3 && 'Cook: Jordan'}
              </motion.span>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="rounded-xl border border-adventure-200 bg-adventure-50 p-4 text-earth-800 shadow-sm dark:border-adventure-600/40 dark:bg-adventure-900/20 dark:text-earth-100"
          animate={{ opacity: [0.75, 1, 0.75] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-earth-800 dark:text-earth-100">
            <Leaf className="h-4 w-4 text-adventure-500 dark:text-adventure-200" />
            Dietary Preferences
          </div>
          <div className="mt-2 grid gap-2 text-xs text-earth-700 dark:text-earth-200">
            <div className="flex justify-between">
              <span>Gluten-Free Ingredients</span>
              <Check className="h-3.5 w-3.5 text-success" />
            </div>
            <div className="flex justify-between">
              <span>Vegetarian Options</span>
              <Check className="h-3.5 w-3.5 text-success" />
            </div>
            <div className="flex justify-between">
              <span>Allergy Alerts</span>
              <motion.span
                className="flex items-center gap-1 rounded-full bg-error/10 px-2 py-0.5 text-error"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Loader2 className="h-3 w-3 animate-spin" />
                Tracking
              </motion.span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

const GEAR_ITEMS = [
  { key: 'recovery', label: 'Recovery Kit',  who: 'Alex M.'   },
  { key: 'water',    label: 'Water System',  who: 'Jordan T.' },
  { key: 'kitchen',  label: 'Camp Kitchen',  who: 'Sam K.'    },
  { key: 'comms',    label: 'CB Radio',      who: 'Jordan T.' },
  { key: 'solar',    label: 'Solar Panels',  who: 'Alex M.'   },
  { key: 'firstaid', label: 'First Aid Kit', who: 'Sam K.'    },
] as const

type GearKey = typeof GEAR_ITEMS[number]['key']

export function GearManagementAnimation(): JSX.Element {
  const [packed, setPacked] = useState<Record<GearKey, boolean>>({
    recovery: true,
    water:    false,
    kitchen:  true,
    comms:    false,
    solar:    false,
    firstaid: true,
  })
  const reducedMotion = useReducedMotion() ?? false
  const toggle = (key: GearKey) => setPacked(p => ({ ...p, [key]: !p[key] }))

  const packedCount = Object.values(packed).filter(Boolean).length
  const total = GEAR_ITEMS.length
  const pct = Math.round((packedCount / total) * 100)
  const allPacked = packedCount === total

  return (
    <div className="overflow-hidden rounded-2xl border border-earth-200 bg-white shadow-xl dark:border-earth-700 dark:bg-earth-900">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-earth-100 px-5 py-3.5 dark:border-earth-800">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-earth-500 dark:text-earth-400">
            Gear checklist
          </p>
          <p className="text-sm font-semibold text-earth-900 dark:text-earth-50">Desert Convoy · 4 crew</p>
        </div>
        <motion.span
          key={String(allPacked)}
          initial={reducedMotion ? false : { opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className={cn(
            'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors duration-500',
            allPacked
              ? 'bg-adventure-50 text-adventure-700 dark:bg-adventure-900/30 dark:text-adventure-300'
              : 'bg-earth-100 text-earth-500 dark:bg-earth-800 dark:text-earth-400',
          )}
        >
          {allPacked ? (
            <Check className="h-3 w-3" />
          ) : (
            <span className="h-1.5 w-1.5 rounded-full bg-earth-400" />
          )}
          {packedCount} / {total} packed
        </motion.span>
      </div>

      {/* Gear rows */}
      <div className="space-y-2.5 border-b border-earth-100 px-5 py-4 dark:border-earth-800">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-earth-400 dark:text-earth-500">
          Who's bringing what
        </p>
        {GEAR_ITEMS.map(({ key, label, who }) => (
          <div key={key} className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <button
                role="checkbox"
                aria-checked={packed[key]}
                aria-label={`Toggle ${label}`}
                onClick={() => toggle(key)}
                className={cn(
                  'relative flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border-2 transition-colors duration-200',
                  packed[key]
                    ? 'border-adventure-500 bg-adventure-500'
                    : 'border-earth-300 bg-white dark:border-earth-600 dark:bg-transparent',
                )}
              >
                {packed[key] && (
                  <motion.svg
                    className="h-2.5 w-2.5 text-white"
                    viewBox="0 0 10 10"
                    fill="none"
                    initial={reducedMotion ? false : { opacity: 0, scale: 0.4 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.15 }}
                    aria-hidden
                  >
                    <path d="M1.5 5L4 7.5 8.5 2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </motion.svg>
                )}
              </button>
              <motion.span
                layout
                className={cn(
                  'text-sm transition-colors duration-200',
                  packed[key] ? 'text-earth-400 line-through dark:text-earth-600' : 'text-earth-700 dark:text-earth-300',
                )}
              >
                {label}
              </motion.span>
            </div>
            <span className="shrink-0 text-[11px] text-earth-400 dark:text-earth-500">{who}</span>
          </div>
        ))}
      </div>

      {/* Progress summary */}
      <div className="bg-earth-50 px-5 py-4 dark:bg-earth-950">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-earth-400 dark:text-earth-500">
            Pack progress
          </p>
          <span className="text-[11px] font-semibold text-earth-600 dark:text-earth-400">{pct}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-earth-200 dark:bg-earth-700">
          <motion.div
            className="h-full rounded-full bg-adventure-500"
            animate={{ width: `${pct}%` }}
            transition={
              reducedMotion
                ? { duration: 0 }
                : { type: 'spring', stiffness: 200, damping: 28 }
            }
          />
        </div>
        <motion.p
          key={`${packedCount}-msg`}
          initial={reducedMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
          className="mt-2.5 text-[11px] text-earth-500 dark:text-earth-400"
        >
          {allPacked
            ? 'All gear accounted for, ready to roll.'
            : `${total - packedCount} item${total - packedCount > 1 ? 's' : ''} still needed`}
        </motion.p>
      </div>
    </div>
  )
}


export function GuestAccessAnimation(): JSX.Element {
  const steps = [
    {
      title: 'Tight link',
      description: 'Turn off GPS pins and route detail for open invites',
      delay: 0
    },
    {
      title: 'They preview',
      description: 'Vet people from the fields you chose to expose',
      delay: 0.4
    },
    {
      title: 'Reveal on trust',
      description: 'Optional join; richer link when you are ready',
      delay: 0.8
    }
  ];

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-earth-200 bg-white p-6 text-earth-900 shadow-xl dark:border-earth-700 dark:bg-earth-900 dark:text-primary-foreground">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(173,158,144,0.12),_transparent_55%)] dark:bg-[radial-gradient(circle_at_top_right,_rgba(115,111,96,0.24),_transparent_55%)]" />
      <div className="relative flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-earth-500 dark:text-earth-100/60">
              Privacy-first sharing
            </p>
            <h3 className="text-lg font-semibold text-earth-900 dark:text-earth-50">
              Coordinates off until you say so
            </h3>
          </div>
          <motion.div
            className="rounded-full bg-earth-800/10 p-3 text-earth-800 dark:bg-earth-200"
            animate={{ rotate: [0, -12, 12, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Lock className="h-5 w-5" />
          </motion.div>
        </div>

        <div className="grid gap-3">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              className="relative overflow-hidden rounded-xl border border-earth-200 bg-earth-50 px-4 py-3 shadow-sm dark:border-earth-700/60 dark:bg-earth-800/80"
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: step.delay }}
            >
              <div className="absolute inset-y-0 left-0 w-1 bg-adventure-400" />
              <div className="ml-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-earth-900 dark:text-earth-100">{step.title}</p>
                  <p className="text-xs text-earth-600 dark:text-earth-400">{step.description}</p>
                </div>
                <motion.div
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-earth-700 shadow-sm dark:bg-earth-900/75 dark:text-earth-100"
                  animate={{ scale: [0.9, 1, 0.9] }}
                  transition={{ duration: 3, repeat: Infinity, delay: index * 0.3 }}
                >
                  {index + 1}
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="rounded-xl border border-earth-300 bg-earth-50 p-4 shadow-sm dark:border-earth-600/50 dark:bg-earth-800/80"
          animate={{ opacity: [0.8, 1, 0.8] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div className="flex items-center justify-between gap-3 text-xs text-earth-700 dark:text-earth-200">
            <div className="flex min-w-0 items-center gap-2">
              <Share2 className="h-4 w-4 shrink-0 text-adventure-600 dark:text-adventure-300" />
              <div className="min-w-0">
                <p className="uppercase tracking-[0.3em] text-earth-500 dark:text-earth-400">
                  Open invite link
                </p>
                <p className="truncate text-sm font-medium text-earth-900 dark:text-earth-100">
                  Desert run · preview only
                </p>
              </div>
            </div>
            <motion.span
              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-earth-900/10 px-3 py-1 font-medium text-earth-900 dark:bg-earth-100/15 dark:text-earth-100"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Lock className="h-3 w-3" />
              Coordinates hidden
            </motion.span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export function DeadEndDiscoveryAnimation(): JSX.Element {
  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-earth-200 bg-white p-6 text-earth-900 shadow-xl dark:border-earth-700 dark:bg-earth-900 dark:text-primary-foreground">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_rgba(92,141,197,0.12),_transparent_55%)] dark:bg-[radial-gradient(circle_at_bottom_left,_rgba(92,141,197,0.2),_transparent_55%)]" />
      <div className="relative flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-earth-500 dark:text-earth-100/60">
              Dispersed campsites
            </p>
            <h3 className="text-lg font-semibold text-earth-900 dark:text-earth-50">
              Dead-end discovery
            </h3>
          </div>
          <motion.div
            className="rounded-full bg-adventure-500/10 p-3 text-adventure-600 dark:text-adventure-100"
            animate={{ y: [0, -3, 0] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <MapPin className="h-5 w-5" />
          </motion.div>
        </div>

        <div className="relative h-40 w-full rounded-xl border border-earth-200 bg-earth-50/80 dark:border-earth-700/60 dark:bg-earth-800/60">
          <svg viewBox="0 0 280 120" className="h-full w-full" aria-hidden>
            <motion.path
              d="M 20 90 L 120 90 L 160 50 L 220 50"
              fill="none"
              stroke="hsl(var(--earth-400))"
              strokeWidth={5}
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.2, ease: 'easeOut' }}
            />
            <motion.path
              d="M 220 50 L 250 50"
              fill="none"
              stroke="hsl(var(--adventure-500))"
              strokeWidth={5}
              strokeLinecap="round"
              strokeDasharray="6 8"
              animate={{ strokeDashoffset: [0, -28] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
            />
          </svg>
          <motion.div
            className="absolute bottom-6 right-8 flex h-9 w-9 items-center justify-center rounded-full bg-adventure-500 text-white shadow-md"
            animate={{ scale: [1, 1.12, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <MapPin className="h-4 w-4" />
          </motion.div>
          <span className="absolute left-3 top-2 text-[10px] font-medium uppercase tracking-wider text-earth-500 dark:text-earth-400">
            Near your route
          </span>
        </div>

        <motion.p
          className="text-xs leading-relaxed text-earth-600 dark:text-earth-300"
          animate={{ opacity: [0.75, 1, 0.75] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          Find quiet pull-offs and cul-de-sac style spurs from OSM: useful backups when Plan A is taken or you want to explore further.
        </motion.p>
      </div>
    </div>
  );
}

const _CAMP_WEATHER_OPTIONS = {
  this: {
    label: 'This weekend',
    stops: [
      { label: 'Indian Heaven', detail: 'Fri · 41°F low · 6mph', score: 9.1, rating: 'Great', tone: 'good' as const },
      { label: 'Crew Meetup', detail: 'Sat · 38°F low · 14mph', score: 7.4, rating: 'Good', tone: 'ok' as const },
      { label: 'Takhlakh Lake', detail: 'Sun · 35°F low · rain pm', score: 5.2, rating: 'Fair', tone: 'warn' as const }
    ]
  },
  next: {
    label: 'Next weekend',
    stops: [
      { label: 'Indian Heaven', detail: 'Fri · 33°F low · 18mph', score: 6.0, rating: 'Good', tone: 'ok' as const },
      { label: 'Crew Meetup', detail: 'Sat · 29°F low · rain am', score: 4.3, rating: 'Fair', tone: 'warn' as const },
      { label: 'Takhlakh Lake', detail: 'Sun · 31°F low · 8mph', score: 7.8, rating: 'Good', tone: 'ok' as const }
    ]
  }
} as const

export function CampspotterWeatherLandingAnimation(): JSX.Element {
  const [dateOption, setDateOption] = useState<keyof typeof _CAMP_WEATHER_OPTIONS>('this')
  const { stops } = _CAMP_WEATHER_OPTIONS[dateOption]

  const toneClasses: Record<'good' | 'ok' | 'warn', string> = {
    good: 'bg-adventure-500/15 text-adventure-600 dark:text-adventure-200',
    ok: 'bg-info/15 text-info',
    warn: 'bg-amber-500/15 text-amber-600 dark:text-amber-300'
  };

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-earth-200 bg-white p-6 text-earth-900 shadow-xl dark:border-earth-700 dark:bg-earth-900 dark:text-primary-foreground">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(92,141,197,0.14),_transparent_60%)] dark:bg-[radial-gradient(circle_at_top,_rgba(92,141,197,0.22),_transparent_60%)]" />
      <div className="relative flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-earth-500 dark:text-earth-100/60">
              Trip weather
            </p>
            <h3 className="text-lg font-semibold text-earth-900 dark:text-earth-50">
              Camp conditions
            </h3>
            <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-info/30 bg-info/10 px-2.5 py-0.5 text-[11px] font-medium text-info">
              <img src="/campspotter.png" alt="" className="h-3.5 w-3.5 rounded-sm" />
              via Campspotter
            </span>
          </div>
          <img src="/campspotter.png" alt="Campspotter" className="h-9 w-9 rounded-lg shadow-sm" />
        </div>

        <div className="inline-flex w-fit gap-1 rounded-lg bg-earth-100 p-1 dark:bg-earth-800">
          {(Object.keys(_CAMP_WEATHER_OPTIONS) as Array<keyof typeof _CAMP_WEATHER_OPTIONS>).map((key) => (
            <button
              key={key}
              onClick={() => setDateOption(key)}
              aria-pressed={dateOption === key}
              className={cn(
                'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                dateOption === key
                  ? 'bg-white text-earth-900 shadow-sm dark:bg-earth-900 dark:text-earth-50'
                  : 'text-earth-500 hover:text-earth-700 dark:hover:text-earth-200'
              )}
            >
              {_CAMP_WEATHER_OPTIONS[key].label}
            </button>
          ))}
        </div>

        <div className="grid gap-2 rounded-xl border border-earth-200 bg-earth-50/90 p-4 dark:border-earth-700/60 dark:bg-earth-800/70">
          {stops.map((stop, index) => (
            <motion.div
              key={`${dateOption}-${stop.label}`}
              className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 shadow-sm dark:bg-earth-900/80"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.08 }}
            >
              <div className="min-w-0">
                <span className="block truncate text-sm font-medium text-earth-800 dark:text-earth-100">{stop.label}</span>
                <span className="block truncate text-xs text-earth-500 dark:text-earth-400">{stop.detail}</span>
              </div>
              <span className={cn('flex-shrink-0 rounded-md px-2 py-1 text-xs font-semibold', toneClasses[stop.tone])}>
                {stop.score.toFixed(1)} · {stop.rating}
              </span>
            </motion.div>
          ))}
        </div>

        <p className="text-xs text-earth-600 dark:text-earth-400">
          Camp scores by <span className="font-medium text-earth-700 dark:text-earth-300">Campspotter</span>, unlocked on Pro, so each stop matches the conditions you actually care about.
        </p>
      </div>
    </div>
  );
}

const _SHARE_TARGETS = [
  { key: 'chat', label: 'Group chat' },
  { key: 'feed', label: 'Your feed' },
  { key: 'download', label: 'Download' }
] as const

export function TripFlythroughShareAnimation(): JSX.Element {
  const prefersReducedMotion = useReducedMotion();
  const [sharedTo, setSharedTo] = useState<string | null>(null)

  useEffect(() => {
    if (!sharedTo) return
    const timer = window.setTimeout(() => setSharedTo(null), 1800)
    return () => window.clearTimeout(timer)
  }, [sharedTo])

  return (
    <div className="relative flex w-full flex-col items-center gap-4 overflow-hidden rounded-2xl border border-earth-200 bg-white p-6 shadow-xl dark:border-earth-700 dark:bg-earth-900">
      <div className="relative w-[220px] max-w-full overflow-hidden rounded-[2.25rem] border-[7px] border-earth-950/85 bg-black shadow-2xl">
        <div className="absolute left-1/2 top-2 z-10 h-1.5 w-16 -translate-x-1/2 rounded-full bg-white/30" />
        <video
          src="https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/trip-flythrough%20(2).webm"
          autoPlay={!prefersReducedMotion}
          loop={!prefersReducedMotion}
          muted
          playsInline
          preload="auto"
          className="block h-auto w-full"
          aria-label="Cinematic 3D flythrough of an overlanding route, ready to share"
        />
      </div>

      <div className="flex items-center gap-2">
        {_SHARE_TARGETS.map((target) => (
          <button
            key={target.key}
            onClick={() => setSharedTo(target.key)}
            className={cn(
              'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
              sharedTo === target.key
                ? 'border-adventure-600 bg-adventure-600 text-white'
                : 'border-earth-200 bg-white text-earth-600 hover:border-adventure-300 hover:text-adventure-700 dark:border-earth-700 dark:bg-earth-900 dark:text-earth-300'
            )}
          >
            {sharedTo === target.key ? 'Shared' : target.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const _PHOTO_SEED_LIKES = [12, 7, 15, 9]

export function PhotoSharingAnimation(): JSX.Element {
  const photos = useMemo(() => {
    const sources = MEDIA_CONFIG.gallery.images.length > 0
      ? MEDIA_CONFIG.gallery.images
      : [MEDIA_CONFIG.mockups.photoSharing];

    const titles = [
      'Hells Canyon',
      'Rollers',
      'Sunsets at Camp',
      'Sunrise Trail'
    ];

    return sources.map((src, index) => ({
      src,
      title: titles[index] ?? `Trip Photo ${index + 1}`
    }));
  }, []);

  const reducedMotion = useReducedMotion() ?? false
  const [activeIndex, setActiveIndex] = useState(0)
  const [likedByMe, setLikedByMe] = useState<boolean[]>(() => photos.map(() => false))
  const [coverIndex, setCoverIndex] = useState(0)
  const activePhoto = photos[activeIndex]

  useEffect(() => {
    if (reducedMotion || photos.length <= 1) return
    const timer = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % photos.length)
    }, 4000)
    return () => window.clearInterval(timer)
  }, [photos.length, reducedMotion])

  const toggleLike = (index: number) =>
    setLikedByMe((prev) => prev.map((liked, i) => (i === index ? !liked : liked)))
  const likeCount = (index: number) => (_PHOTO_SEED_LIKES[index] ?? 5) + (likedByMe[index] ? 1 : 0)

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-earth-200 bg-white p-6 text-earth-900 shadow-xl dark:border-earth-700 dark:bg-earth-900 dark:text-primary-foreground">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(92,141,197,0.12),_transparent_60%)] dark:bg-[radial-gradient(circle_at_center,_rgba(92,141,197,0.18),_transparent_60%)]" />
      <div className="relative grid gap-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-earth-500 dark:text-earth-100/60">
              Trip Gallery
            </p>
            <h3 className="text-lg font-semibold text-earth-900 dark:text-earth-50">
              Hells Canyon Highlights
            </h3>
          </div>
          <Camera className="h-5 w-5 text-adventure-500 dark:text-adventure-300" />
        </div>

        <div className="relative h-48 overflow-hidden rounded-xl border border-earth-200 dark:border-earth-700/60">
          <motion.div
            key={activePhoto.src}
            className="absolute inset-0 flex h-full w-full flex-col justify-between bg-cover bg-center p-4"
            style={{ backgroundImage: `linear-gradient(160deg, rgba(0,0,0,0.5), rgba(0,0,0,0.05)), url(${activePhoto.src})` }}
            initial={reducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {coverIndex === activeIndex && (
              <span className="w-fit rounded-full bg-adventure-500 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white">
                Cover
              </span>
            )}
            <div className="flex items-end justify-between">
              <div>
                <p className="text-sm font-semibold text-white">{activePhoto.title}</p>
                <p className="text-xs text-white/70">Captured by Crew</p>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCoverIndex(activeIndex)}
                  aria-pressed={coverIndex === activeIndex}
                  aria-label="Set as trip cover"
                  className={cn(
                    'flex h-7 w-7 items-center justify-center rounded-full transition-colors',
                    coverIndex === activeIndex ? 'bg-adventure-500 text-white' : 'bg-white/15 text-white hover:bg-white/25'
                  )}
                >
                  <Star className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => toggleLike(activeIndex)}
                  aria-pressed={likedByMe[activeIndex]}
                  aria-label="Like photo"
                  className={cn(
                    'flex items-center gap-1 rounded-full px-2 py-1 transition-colors',
                    likedByMe[activeIndex] ? 'bg-white/25 text-white' : 'bg-white/15 text-white/80 hover:bg-white/25'
                  )}
                >
                  <Heart className="h-3.5 w-3.5" />
                  <span className="text-[11px] font-medium">{likeCount(activeIndex)}</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="flex items-center justify-center gap-1.5">
          {photos.map((photo, index) => (
            <button
              key={photo.src}
              onClick={() => setActiveIndex(index)}
              aria-label={`Show ${photo.title}`}
              aria-current={activeIndex === index}
              className={cn(
                'h-1.5 rounded-full transition-all',
                activeIndex === index ? 'w-5 bg-adventure-500' : 'w-1.5 bg-earth-200 dark:bg-earth-700'
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

